import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Home from './components/Home';
import GiveawayCard from './components/GiveawayCard';
import GiveawayForm from './components/GiveawayForm';
import Login from './components/Login';
import Signup from './components/Signup';
import AdminPanel from './components/AdminPanel';
import ThemeSelector, { themes } from './components/ThemeSelector';
// Removed PricingTable import (was unused)
import ExtraEntries from './components/ExtraEntries';
import AnnouncementBoard from './components/AnnouncementBoard';
import AdvertisingPage from './components/AdvertisingPage';
import CookieConsent from './components/CookieConsent';
import StaffTeam from './components/StaffTeam';
import Chat from './components/Chat';
import UserProfile from './components/UserProfile';
import PrivateMessages from './components/PrivateMessages';
import GiveawayCreatePanel from './components/GiveawayCreatePanel';
import WikiDrop from './components/WikiDrop';
import HostingServices from './components/HostingServices';
import DevelopmentServices from './components/DevelopmentServices';
import Credits from './components/Credits';
import Settings from './components/Settings';
import Dashboard from './components/Dashboard';
import Leaderboard from './components/Leaderboard';
import Achievements from './components/Achievements';
import FounderDashboard from './components/FounderDashboard';
import ActivityLog from './components/ActivityLog';
import Notifications from './components/Notifications';
import StatsWidget from './components/StatsWidget';
import ErrorBoundary from './components/ErrorBoundary';
import ToastContainer from './components/ToastContainer';
import LoadingSpinner from './components/LoadingSpinner';
import System from './components/System';
import { giveawaysAPI, authAPI } from './utils/api';
import { ensureRankDefaults } from './utils/rankConfig';
import { hasPermission, PERMISSIONS } from './utils/permissions';

const logo = '/logo.png';

const FOUNDER_USERNAME = 'Founder';
const FOUNDER_PASSWORD = 'NitroDrop2024!@#$%^&*()_+SecurePass';
const COFOUNDER_USERNAME = 'CoFounder';
const COFOUNDER_PASSWORD = 'NitroDropCo2024!@#$%^&*()_+SecurePass';

// Move initializeTheme BEFORE any place it's called.
// Convert initializeTheme to a function declaration that accepts the active theme as an argument.
function applyTheme(theme) {
  const selectedTheme = themes.find(t => t.id === theme) || themes[0];
  const root = document.documentElement;
  root.style.setProperty('--primary', selectedTheme.colors.primary);
  root.style.setProperty('--bg', selectedTheme.colors.bg);
  root.style.setProperty('--box-bg', selectedTheme.colors.boxBg);

  if (theme === 'white') {
    root.style.setProperty('--text', '#1a1a1a');
    root.style.setProperty('--dim', '#666666');
    root.style.setProperty('--border', '#e0e0e0');
    root.style.setProperty('--input-bg', '#fafafa');
  } else {
    root.style.setProperty('--text', '#eff0f3');
    root.style.setProperty('--dim', '#aab1c7');
    root.style.setProperty('--border', '#2c2e43');
    root.style.setProperty('--input-bg', '#232539');
  }
}

function initializeTheme(theme) {
  applyTheme(theme);
}

function App() {
  // Before rendering, apply initial theme immediately to prevent FOUC/blue screen
  const getInitialTheme = () => {
    try {
      return localStorage.getItem('nitroTheme') || 'dark';
    } catch {
      return 'dark';
    }
  };
  const [theme, setTheme] = useState(() => {
    const initialTheme = getInitialTheme();
    // Apply the theme ASAP to avoid blank/incorrect theme flash
    if (typeof window !== 'undefined') {
      initializeTheme(initialTheme);
    }
    return initialTheme;
  });

  const [currentUser, setCurrentUser] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [giveaways, setGiveaways] = useState([]);
  const [selectedGiveaway, setSelectedGiveaway] = useState(null);
  const [showProfile, setShowProfile] = useState(null);
  // REMOVED showMessages state (was unused)
  const [showCreateGiveaway, setShowCreateGiveaway] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });
  // FIX: Make desktop default for nav state (open), mobile default is false. Only open nav by default on desktop.
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth > 768 ? true : false;
    }
    return false;
  });
  const [stats, setStats] = useState({
    totalGiveaways: 0,
    totalParticipants: 0,
    totalPrizes: 0,
  });
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Util: roles with admin panel access
  const adminRoles = [
    'founder',
    'co-founder',
    'manager',
    'administrator',
    'head-admin',
    'admin',
  ];
  const canEditGiveaways = !!currentUser && (currentUser.role === 'founder' || hasPermission(currentUser, 'manage_giveaways'));

  // Define updaters and callbacks *before* they are used inside useEffect
  const updateStats = useCallback((giveawaysList) => {
    let active = giveawaysList.filter(g => g.status === 'active');
    let totalParticipants = 0;
    active.forEach(giveaway => {
      totalParticipants += giveaway.participants || 0;
    });
    setStats({
      totalGiveaways: active.length,
      totalParticipants,
      totalPrizes: active.length,
    });
  }, []);

  // Toast management
  const addToast = useCallback((toast) => {
    setToasts(prev => [...prev, toast]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Move below the updateStats definition
  const loadGiveaways = useCallback(async () => {
    setLoading(true);
    try {
      const data = await giveawaysAPI.getActive();
      setGiveaways(data);
      updateStats(data);
    } catch (error) {
      console.error('Failed to load giveaways:', error);
      addToast({ id: Date.now(), message: 'Failed to load giveaways', type: 'error' });
      setGiveaways([]);
      updateStats([]);
    } finally {
      setLoading(false);
    }
  }, [updateStats, addToast]);

  const loadCurrentUser = useCallback(async () => {
    try {
      const response = await authAPI.getCurrentUser();
      if (response.user) {
        setCurrentUser(response.user);
      }
    } catch (error) {
      setCurrentUser(null);
    }
  }, []);

  const checkAutoLogin = useCallback(async () => {
    try {
      const cookieConsent = localStorage.getItem('cookieConsent');
      if (cookieConsent === 'accepted') {
        const response = await authAPI.getCurrentUser();
        if (response.user && !response.user.locked) {
          setCurrentUser(response.user);
        }
      }
    } catch {
      // ignore for now
    }
  }, []);

  // One-time initialization
  useEffect(() => {
    ensureRankDefaults();
    initializeTheme(theme);
    loadGiveaways();
    loadCurrentUser();
    checkAutoLogin();
    // Only list local callback dependencies here; avoid listing literals.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Functions are stable via useCallback or are top-level imports
    theme,
    loadGiveaways,
    loadCurrentUser,
    checkAutoLogin,
  ]);

  // Theme sync effect
  useEffect(() => {
    initializeTheme(theme);
    try {
      localStorage.setItem('nitroTheme', theme);
    } catch {/* ignore for now */}
    // Only list what is actually used.
  }, [theme]);

  // Mobile viewport
  useEffect(() => {
    function handleResize() {
      const isMobile = window.innerWidth <= 768;
      setIsMobileViewport(isMobile);
      // On desktop, always open nav; on mobile, close nav on resize.
      setIsMobileNavOpen(!isMobile);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      handleResize();
      return () => window.removeEventListener('resize', handleResize);
    }
    return undefined;
  }, []);

  useEffect(() => {
    updateStats(giveaways);
  }, [giveaways, updateStats]);

  // User cross-tab sync
  useEffect(() => {
    async function syncUser() {
      try {
        const response = await authAPI.getCurrentUser();
        if (response.user) {
          setCurrentUser(response.user);
        }
      } catch {/* ignore */}
    }
    function handleStorage(event) {
      if (!event || !event.key) return;
      if (event.key === 'nitroToken' || event.key === 'nitroCurrentUser')
        syncUser();
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorage);
      return () => window.removeEventListener('storage', handleStorage);
    }
    return undefined;
  }, []);

  // Founder accounts are now initialized on the backend

  const handleCookieAccept = () => {
    checkAutoLogin();
  };

  const closeMobileNav = () => {
    if (isMobileViewport) setIsMobileNavOpen(false);
  };

  const toggleMobileNav = () => {
    setIsMobileNavOpen(prev => (isMobileViewport ? !prev : prev));
  };

  const handleTabChange = tab => {
    setActiveTab(tab);
    closeMobileNav();
  };

  const handleLogin = user => {
    if (user) {
      setCurrentUser(user);
      setShowAuth(false);
    } else {
      setShowAuth(false);
    }
  };

  const handleSignup = user => {
    if (user) {
      setCurrentUser(user);
      setShowAuth(false);
    } else {
      setShowAuth(false);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    setCurrentUser(null);
    setShowAdminPanel(false);
    closeMobileNav();
  };

  const handleEnterGiveaway = giveaway => {
    if (!currentUser) {
      setAuthMode('login');
      setShowAuth(true);
      return;
    }
    setSelectedGiveaway(giveaway);
  };

  const handleCloseModal = () => {
    setSelectedGiveaway(null);
  };

  const handleFormSubmit = async formData => {
    if (!currentUser || !selectedGiveaway) return;

    try {
      // Enter giveaway via API
      const response = await giveawaysAPI.enter(selectedGiveaway._id || selectedGiveaway.id);
      
      // Update local state
      setCurrentUser(response.user);
      await loadGiveaways();

    // Webhook notification
    try {
      const embed = {
        title: `🎉 New Entry: ${selectedGiveaway.title}`,
        color: 0x6361e7,
        fields: [
          {
            name: 'Discord Username',
            value: formData.discorduser || currentUser.discordUsername,
            inline: false,
          },
          { name: 'Age', value: formData.age || 'N/A', inline: true },
          {
            name: 'Contact Method',
            value: formData.contact || 'N/A',
            inline: true,
          },
          {
            name: 'Why / What does Nitro mean?',
            value: formData.why || 'N/A',
            inline: false,
          },
          {
            name: 'Discord Activity',
            value: formData.activity || 'N/A',
            inline: true,
          },
          {
            name: 'NitroDrop Member?',
            value: formData.community || 'N/A',
            inline: true,
          },
        ],
        footer: { text: 'Submitted on ' + new Date().toLocaleString() },
      };

      if (formData.contact === 'email' && formData.email) {
        embed.fields.splice(
          3,
          0,
          { name: 'Email', value: formData.email, inline: false }
        );
      }
      if (formData.contact === 'other' && formData.otherContact) {
        embed.fields.splice(
          3,
          0,
          { name: 'Other Contact', value: formData.otherContact, inline: false }
        );
      }

      const payload = {
        username: 'NitroDrop Entry',
        avatar_url: 'https://nitrodrop.vercel.app/logo.png',
        embeds: [embed],
      };

      fetch(
        'https://discord.com/api/webhooks/1442524296764325968/dbzqrpSmCHPYM90ckTKMsE7rGsED1jzzI3hGUF6CP1TSnqhq_5xpZlx0rsi0LJ8KeiL0',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      ).catch(() => {});
    } catch {
      // ignore webhook failures
    }
    setSelectedGiveaway(null);
    } catch (error) {
      console.error('Failed to enter giveaway:', error);
      alert('Failed to enter giveaway. Please try again.');
    }
  };

  const formatTimeRemaining = endsAt => {
    const now = new Date();
    const end = new Date(endsAt);
    const diff = end - now;
    if (diff <= 0) return 'Ended';
    const days = Math.floor(diff / 864e5);
    const hours = Math.floor((diff % 864e5) / 36e5);
    const minutes = Math.floor((diff % 36e5) / 6e4);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Render
  return (
    <ErrorBoundary>
      <div className="app">
      <header className="header">
        <div className="header-content">
          <a href="/" className="logo">
            <img src={logo} alt="NitroDrop" />
            <span>NitroDrop</span>
          </a>
          {isMobileViewport && (
            <button
              className="mobile-nav-toggle"
              onClick={toggleMobileNav}
              aria-label="Toggle navigation"
            >
              {isMobileNavOpen ? 'Close Menu' : 'Menu'}
            </button>
          )}
          <nav
            className={`main-nav ${
              isMobileViewport ? 'is-mobile' : ''
            } ${isMobileNavOpen ? 'open' : 'closed'}`}
          >
            {isMobileViewport && (
              <div className="mobile-nav-header">
                <span>Navigation</span>
                <button
                  className="mobile-nav-close"
                  onClick={() => setIsMobileNavOpen(false)}
                  aria-label="Close navigation"
                >
                  ×
                </button>
              </div>
            )}
            <ul className="nav">
              <li>
                <a
                  href="#home"
                  className={activeTab === 'home' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('home');
                  }}
                >
                  Home
                </a>
              </li>
              <li>
                <a
                  href="#giveaways"
                  className={activeTab === 'giveaways' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('giveaways');
                  }}
                >
                  Giveaways
                </a>
              </li>
              <li>
                <a
                  href="#announcements"
                  className={activeTab === 'announcements' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('announcements');
                  }}
                >
                  📢 Announcements
                </a>
              </li>
              {currentUser && (
                <li>
                  <a
                    href="#notifications"
                    className={activeTab === 'notifications' ? 'active' : ''}
                    onClick={e => {
                      e.preventDefault();
                      handleTabChange('notifications');
                    }}
                  >
                    🔔 Notifications
                  </a>
                </li>
              )}
              <li>
                <a
                  href="#advertising"
                  className={activeTab === 'advertising' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('advertising');
                  }}
                >
                  Buy Ad Space
                </a>
              </li>
              <li>
                <a
                  href="#extra-entries"
                  className={activeTab === 'extra-entries' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('extra-entries');
                  }}
                >
                  Extra Entries
                </a>
              </li>
              <li>
                <a
                  href="#rules"
                  className={activeTab === 'rules' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('rules');
                  }}
                >
                  Rules
                </a>
              </li>
              <li>
                <a
                  href="#staff"
                  className={activeTab === 'staff' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('staff');
                  }}
                >
                  Staff Team
                </a>
              </li>
              <li>
                <a
                  href="#chat"
                  className={activeTab === 'chat' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('chat');
                  }}
                >
                  Chat
                </a>
              </li>
              <li>
                <a
                  href="#messages"
                  className={activeTab === 'messages' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('messages');
                  }}
                >
                  Messages
                </a>
              </li>
              <li>
                <a
                  href="#wikidrop"
                  className={activeTab === 'wikidrop' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('wikidrop');
                  }}
                >
                  📚 WikiDrop
                </a>
              </li>
              {currentUser && (
                <li>
                  <a
                    href="#system"
                    className={activeTab === 'system' ? 'active' : ''}
                    onClick={e => {
                      e.preventDefault();
                      handleTabChange('system');
                    }}
                  >
                    ⚙️ System
                  </a>
                </li>
              )}
              <li>
                <a
                  href="#hosting"
                  className={activeTab === 'hosting' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('hosting');
                  }}
                >
                  🌐 Hosting
                </a>
              </li>
              <li>
                <a
                  href="#development"
                  className={activeTab === 'development' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('development');
                  }}
                >
                  💻 Development
                </a>
              </li>
              <li>
                <a
                  href="#credits"
                  className={activeTab === 'credits' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('credits');
                  }}
                >
                  🙏 Credits
                </a>
              </li>
              {currentUser && (
                <li>
                  <a
                    href="#dashboard"
                    className={activeTab === 'dashboard' ? 'active' : ''}
                    onClick={e => {
                      e.preventDefault();
                      handleTabChange('dashboard');
                    }}
                  >
                    📊 Dashboard
                  </a>
                </li>
              )}
              <li>
                <a
                  href="#leaderboard"
                  className={activeTab === 'leaderboard' ? 'active' : ''}
                  onClick={e => {
                    e.preventDefault();
                    handleTabChange('leaderboard');
                  }}
                >
                  🏆 Leaderboard
                </a>
              </li>
              {currentUser && (currentUser.role === 'founder' || hasPermission(currentUser, PERMISSIONS.VIEW_ANALYTICS)) && (
                <li>
                  <a
                    href="#activity-log"
                    className={activeTab === 'activity-log' ? 'active' : ''}
                    onClick={e => {
                      e.preventDefault();
                      handleTabChange('activity-log');
                    }}
                  >
                    📋 Activity Log
                  </a>
                </li>
              )}
              {currentUser && (
                <li>
                  <a
                    href="#achievements"
                    className={activeTab === 'achievements' ? 'active' : ''}
                    onClick={e => {
                      e.preventDefault();
                      handleTabChange('achievements');
                    }}
                  >
                    🏅 Achievements
                  </a>
                </li>
              )}
              {currentUser && (
                <>
                  <li>
                    <button
                      className="profile-btn"
                      onClick={() => {
                        setShowProfile(currentUser.id);
                        closeMobileNav();
                      }}
                    >
                      Profile
                    </button>
                  </li>
                  <li>
                    <button
                      className="settings-btn"
                      onClick={() => {
                        setShowSettings(true);
                        closeMobileNav();
                      }}
                    >
                      ⚙️ Settings
                    </button>
                  </li>
                </>
              )}
              {canEditGiveaways && (
                <li>
                  <button
                    className="create-giveaway-btn"
                    onClick={() => {
                      setShowCreateGiveaway(true);
                      closeMobileNav();
                    }}
                  >
                    Create Giveaway
                  </button>
                </li>
              )}
              <li>
                <ThemeSelector currentTheme={theme} onThemeChange={setTheme} />
              </li>
              {currentUser ? (
                <>
                  <li>
                    <span className="user-info">
                      {currentUser.discordUsername}
                    </span>
                  </li>
                  {adminRoles.includes(currentUser.role) && (
                    <>
                      <li>
                        <button
                          className="admin-btn"
                          onClick={() => {
                            setShowAdminPanel(true);
                            closeMobileNav();
                          }}
                        >
                          Admin Panel
                        </button>
                      </li>
                      {currentUser.role === 'founder' && (
                        <li>
                          <a
                            href="#founder-dashboard"
                            className={activeTab === 'founder-dashboard' ? 'active' : ''}
                            onClick={e => {
                              e.preventDefault();
                              handleTabChange('founder-dashboard');
                            }}
                          >
                            👑 Founder Dashboard
                          </a>
                        </li>
                      )}
                    </>
                  )}
                  <li>
                    <button className="logout-btn" onClick={handleLogout}>
                      Logout
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <button
                      className="auth-btn"
                      onClick={() => {
                        setAuthMode('login');
                        setShowAuth(true);
                        closeMobileNav();
                      }}
                    >
                      Login
                    </button>
                  </li>
                  <li>
                    <button
                      className="auth-btn signup-btn"
                      onClick={() => {
                        setAuthMode('signup');
                        setShowAuth(true);
                        closeMobileNav();
                      }}
                    >
                      Sign Up
                    </button>
                  </li>
                </>
              )}
              <li>
                <a
                  href="https://discord.gg/RXVZZWzMcq"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="discord-btn"
                  onClick={closeMobileNav}
                >
                  Join Discord
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      <main className="container">
        <div
          className="tab-content"
          style={{ display: activeTab === 'home' ? 'block' : 'none' }}
        >
          <Home onNavigate={setActiveTab} giveaways={giveaways} stats={stats} />
        </div>

        <div
          className="tab-content"
          style={{ display: activeTab === 'giveaways' ? 'block' : 'none' }}
        >
          <div className="hero">
            <h1 className="animate-fade-in">
              Discord Nitro &amp; Cash Giveaways
            </h1>
            <p className="animate-fade-in-delay">
              Win Discord Nitro, PayPal cash, and Steam gift cards. Multiple
              active giveaways with fair draws.
            </p>

            <StatsWidget stats={stats} />
          </div>
        </div>

        <div
          className="tab-content"
          style={{ display: activeTab === 'announcements' ? 'block' : 'none' }}
        >
          <AnnouncementBoard currentUser={currentUser} />
        </div>

        <div
          className="tab-content"
          style={{ display: activeTab === 'advertising' ? 'block' : 'none' }}
        >
          <AdvertisingPage currentUser={currentUser} />
        </div>

        <div
          className="tab-content"
          style={{ display: activeTab === 'giveaways' ? 'block' : 'none' }}
        >
          <div className="giveaways-section">
            <h2 className="section-title">Active Giveaways</h2>
            <div className="giveaways-grid">
              {giveaways
                .filter(g => g.status === 'active')
                .map((giveaway, index) => (
                  <div
                    key={giveaway.id}
                    className="animate-card"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <GiveawayCard
                      giveaway={giveaway}
                      onEnter={() => handleEnterGiveaway(giveaway)}
                      formatTimeRemaining={formatTimeRemaining}
                      currentUser={currentUser}
                      onViewProfile={setShowProfile}
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>

        <div
          className="tab-content"
          style={{ display: activeTab === 'extra-entries' ? 'block' : 'none' }}
        >
          <ExtraEntries />
        </div>

        <div
          className="tab-content"
          style={{ display: activeTab === 'rules' ? 'block' : 'none' }}
        >
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 className="section-title">Giveaway Rules</h2>
            <ul className="rules-list">
              <li>
                <b>One entry per giveaway per person</b> – no alt accounts or
                abuse.
              </li>
              <li>
                Use your real Discord username and valid contact information.
              </li>
              <li>
                Fake or spam entries will be disqualified instantly.
              </li>
              <li>
                Winners must reply within <b>24 hours</b> when contacted.
              </li>
              <li>
                Being a member of our Discord server may be required to claim
                prizes.
              </li>
              <li>Winners are picked via random draw or wheel spin.</li>
              <li>
                This site is not affiliated with Discord, Inc. or any related
                entity.
              </li>
              <li>
                Rules may be updated to keep things fair for all participants.
              </li>
            </ul>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <a
                href="https://discord.gg/RXVZZWzMcq"
                target="_blank"
                rel="noopener noreferrer"
                className="discord-btn"
              >
                Join Discord Server
              </a>
            </div>
          </div>
        </div>

        <div
          className="tab-content"
          style={{ display: activeTab === 'staff' ? 'block' : 'none' }}
        >
          <StaffTeam onViewProfile={setShowProfile} />
        </div>

        <div
          className="tab-content"
          style={{ display: activeTab === 'chat' ? 'block' : 'none' }}
        >
          <Chat currentUser={currentUser} onViewProfile={setShowProfile} />
        </div>

        <div
          className="tab-content"
          style={{ display: activeTab === 'messages' ? 'block' : 'none' }}
        >
          <PrivateMessages
            currentUser={currentUser}
            onViewProfile={setShowProfile}
          />
        </div>

        <div
          className="tab-content"
          style={{ display: activeTab === 'wikidrop' ? 'block' : 'none' }}
        >
          <WikiDrop />
        </div>

        {currentUser && (
          <div
            className="tab-content"
            style={{ display: activeTab === 'system' ? 'block' : 'none' }}
          >
            <System currentUser={currentUser} onNavigate={setActiveTab} />
          </div>
        )}

        <div
          className="tab-content"
          style={{ display: activeTab === 'hosting' ? 'block' : 'none' }}
        >
          <HostingServices />
        </div>

        <div
          className="tab-content"
          style={{ display: activeTab === 'development' ? 'block' : 'none' }}
        >
          <DevelopmentServices />
        </div>

        <div
          className="tab-content"
          style={{ display: activeTab === 'credits' ? 'block' : 'none' }}
        >
          <Credits />
        </div>

        {currentUser && (
          <div
            className="tab-content"
            style={{ display: activeTab === 'dashboard' ? 'block' : 'none' }}
          >
            <Dashboard currentUser={currentUser} giveaways={giveaways} />
          </div>
        )}

        <div
          className="tab-content"
          style={{ display: activeTab === 'leaderboard' ? 'block' : 'none' }}
        >
          <Leaderboard currentUser={currentUser} />
        </div>

        {currentUser && (
          <div
            className="tab-content"
            style={{ display: activeTab === 'achievements' ? 'block' : 'none' }}
          >
            <Achievements currentUser={currentUser} />
          </div>
        )}

        {currentUser && (
          <div
            className="tab-content"
            style={{ display: activeTab === 'notifications' ? 'block' : 'none' }}
          >
            <Notifications />
          </div>
        )}

        {currentUser && currentUser.role === 'founder' && (
          <div
            className="tab-content"
            style={{ display: activeTab === 'founder-dashboard' ? 'block' : 'none' }}
          >
            <FounderDashboard currentUser={currentUser} />
          </div>
        )}

        {currentUser && (currentUser.role === 'founder' || hasPermission(currentUser, PERMISSIONS.VIEW_ANALYTICS)) && (
          <div
            className="tab-content"
            style={{ display: activeTab === 'activity-log' ? 'block' : 'none' }}
          >
            <ActivityLog />
          </div>
        )}
      </main>

      {showAuth && authMode === 'login' && (
        <Login
          onLogin={handleLogin}
          onSwitchToSignup={() => setAuthMode('signup')}
        />
      )}

      {showAuth && authMode === 'signup' && (
        <Signup
          onSignup={handleSignup}
          onSwitchToLogin={() => setAuthMode('login')}
        />
      )}

      {showAdminPanel && currentUser && adminRoles.includes(currentUser.role) && (
        <AdminPanel
          onClose={() => setShowAdminPanel(false)}
          currentUser={currentUser}
        />
      )}

      {selectedGiveaway && (
        <GiveawayForm
          giveaway={selectedGiveaway}
          onClose={handleCloseModal}
          onSubmit={handleFormSubmit}
        />
      )}

      <CookieConsent onAccept={handleCookieAccept} />

      {showProfile && (
        <UserProfile
          userId={showProfile}
          currentUser={currentUser}
          onClose={() => setShowProfile(null)}
          onMessage={user => {
            setShowProfile(null);
            setActiveTab('messages');
          }}
        />
      )}

      {showCreateGiveaway && (
        <GiveawayCreatePanel
          currentUser={currentUser}
          onClose={() => setShowCreateGiveaway(false)}
          onCreated={() => {
            loadGiveaways();
            setActiveTab('giveaways');
          }}
        />
      )}

      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal-content settings-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowSettings(false)}>×</button>
            <Settings
              currentUser={currentUser}
              onThemeChange={setTheme}
              currentTheme={theme}
            />
          </div>
        </div>
      )}

      <footer className="footer">
        <p>
          &copy; {new Date().getFullYear()} NitroDrop. Not affiliated with
          Discord.
        </p>
        <p style={{ marginTop: '0.5rem' }}>
          Made by <b>0b.livion</b> |{' '}
          <a
            href="https://discord.gg/RXVZZWzMcq"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#5865f2' }}
          >
            Join Server
          </a>
        </p>
      </footer>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
      {loading && <LoadingSpinner message="Loading..." />}
    </div>
    </ErrorBoundary>
  );
}

export default App;
